/**
 * Resources slice
 */

import { createAction, createSlice } from '@reduxjs/toolkit';
import { Resource } from '../../store/slices/types';

const initialState = {
  resources: [] as Resource[],
  currentResource: null as Resource | null,
  loading: false,
  error: null as string | null,
  uploadProgress: 0,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
  },
};

const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    setResources(state, action) {
      // Handle both array response and paginated response
      if (Array.isArray(action.payload)) {
        state.resources = action.payload;
      } else if (action.payload?.data) {
        state.resources = action.payload.data;
        if (action.payload.pagination) {
          state.pagination = {
            total: action.payload.pagination.total || action.payload.total || 0,
            page: action.payload.pagination.page || action.payload.page || 1,
            limit: action.payload.pagination.limit || action.payload.limit || 10,
          };
        }
      } else {
        state.resources = [];
      }
      state.loading = false;
      state.error = null;
    },
    setCurrentResource(state, action) {
      state.currentResource = action.payload;
      state.loading = false;
      state.error = null;
    },
    addResource(state, action) {
      state.resources.push(action.payload);
      state.loading = false;
      state.error = null;
    },
    updateResource(state, action) {
      const index = state.resources.findIndex(
        (r) => (r._id || r.id) === (action.payload._id || action.payload.id)
      );
      if (index !== -1) {
        state.resources[index] = action.payload;
      }
      if (state.currentResource && (state.currentResource._id || state.currentResource.id) === (action.payload._id || action.payload.id)) {
        state.currentResource = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removeResource(state, action) {
      state.resources = state.resources.filter(
        (r) => (r._id || r.id) !== action.payload
      );
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
    setUploadProgress(state, action) {
      state.uploadProgress = action.payload;
    },
    clearUploadProgress(state) {
      state.uploadProgress = 0;
    },
  },
});

// Reducer
export const resourcesReducer = resourcesSlice.reducer;

// Actions
export const {
  setResources,
  setCurrentResource,
  addResource,
  updateResource,
  removeResource,
  setLoading,
  setError,
  clearError,
  setUploadProgress,
  clearUploadProgress,
} = resourcesSlice.actions;

// Async Actions
export const fetchResources = createAction<{ page?: number; limit?: number; activeOnly?: boolean } | undefined>('RESOURCES/FETCH_ALL');
export const fetchResource = createAction<string>('RESOURCES/FETCH_ONE');
export const createResource = createAction<Omit<Resource, '_id' | 'id'>>('RESOURCES/CREATE');
export const updateResourceAction = createAction<{ id: string; data: Partial<Resource> }>('RESOURCES/UPDATE');
export const deleteResource = createAction<string>('RESOURCES/DELETE');

